import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moveCursor, { cursorForCell, firstCursor, resolveCursor } from 'utilities/ledger/gridCursor';
import {
	addSelectionAt,
	extendSelectionTo,
	isCellSelected,
	selectionAt,
	summariseSelection,
} from 'utilities/ledger/gridSelection';
import LedgerSelectionSummary from './LedgerSelectionSummary';

const LedgerGridContext = createContext(null);

export function useLedgerGrid() {
	return useContext(LedgerGridContext);
}

/**
 * Selection state for the whole ledger, shared by both tables.
 *
 * One cursor rather than one per table, because the two are one sheet as far as the keyboard is
 * concerned -- right off the budget block's last column continues into the recordings, which is
 * what her A:D / E:L layout does.
 *
 * The provider holds no DOM and no key handling. Keys arrive at the selected `<td>`, which is a
 * real focused element (see LedgerCell), so a cell's own commit and clear callbacks are already in
 * scope where the key lands. A container-level handler would have had to reach back into the cell
 * for those, and the reaching is the part that goes wrong.
 *
 * The one piece of DOM it does own is the drag: a shift-drag across a block of cells starts on one
 * cell and ends on another, and can end outside the table entirely, so the mouseup that finishes
 * it has to be listened for on the window rather than on any cell.
 */
export default function LedgerGrid({ panes, className, children }) {
	const [selection, setSelection] = useState(null);
	const [editing, setEditing] = useState(null);
	// Until the ledger has been touched it draws a cursor but does not take focus. Mounting is a
	// tab being opened, not a request to type into the grid, and a cell that focuses itself on
	// mount scrolls the page down to the ledger and takes the keyboard off whatever had it. Roving
	// tabindex means the selected cell is still where a Tab from outside lands.
	const [engaged, setEngaged] = useState(false);
	const dragging = useRef(false);

	const cursor = selection ? selection.cursor : null;
	const at = useMemo(() => resolveCursor(panes, cursor) || resolveCursor(panes, firstCursor(panes)), [panes, cursor]);
	const summary = useMemo(() => summariseSelection(panes, selection), [panes, selection]);

	useEffect(() => {
		// Anywhere at all: a drag released over the page background, or outside the window
		// entirely, still ends the drag. Without this the next mouse move over the grid carries on
		// extending the selection with no button held.
		const release = () => {
			dragging.current = false;
		};
		window.addEventListener('mouseup', release);
		return () => window.removeEventListener('mouseup', release);
	}, []);

	/**
	 * @param mode {string} replace (a plain click), extend (shift) or add (ctrl) -- what the
	 *                      modifier keys on a click mean, and the three ways a selection grows
	 */
	const select = useCallback((paneIndex, rowIndex, cellIndex, mode = 'replace') => {
		const next = cursorForCell(panes, paneIndex, rowIndex, cellIndex);
		if(!next) {
			return;
		}
		setEngaged(true);
		setEditing(null);
		setSelection((current) => {
			if(mode === 'extend') {
				return extendSelectionTo(current, next);
			}
			if(mode === 'add') {
				return addSelectionAt(current, next);
			}
			return selectionAt(next);
		});
	}, [panes]);

	/** @param extend {boolean} true when shift is held, so the range grows instead of moving */
	const move = useCallback((direction, extend = false) => {
		setEngaged(true);
		setEditing(null);
		setSelection((current) => {
			const base = current || selectionAt(firstCursor(panes));
			if(!base) {
				return current;
			}
			const next = moveCursor(panes, base.cursor, direction);
			return extend ? extendSelectionTo(base, next) : selectionAt(next);
		});
	}, [panes]);

	const beginDrag = useCallback(() => {
		dragging.current = true;
	}, []);

	/** A cell the mouse has been dragged over: the same extension shift-clicking it would make. */
	const dragTo = useCallback((paneIndex, rowIndex, cellIndex) => {
		if(!dragging.current) {
			return;
		}
		select(paneIndex, rowIndex, cellIndex, 'extend');
	}, [select]);

	const selected = useCallback((paneIndex, row, cellIndex) => isCellSelected(selection, paneIndex, row, cellIndex),
		[selection]);

	/** @param initial {string|null} the character that started the edit, when typing began it */
	const beginEdit = useCallback((initial = null) => {
		setEngaged(true);
		setEditing({ initial });
	}, []);

	/**
	 * Leave edit mode. `direction` is where the key that ended the edit sends the cursor -- Enter
	 * down, Tab right -- and moving is what returns focus to a plain cell, since the selected cell
	 * takes focus on its own once nothing is being edited.
	 */
	const endEdit = useCallback((direction = null) => {
		setEngaged(true);
		setEditing(null);
		if(direction) {
			setSelection((current) => {
				const base = current || selectionAt(firstCursor(panes));
				return base ? selectionAt(moveCursor(panes, base.cursor, direction)) : current;
			});
		}
	}, [panes]);

	const value = useMemo(() => ({
		selection: at,
		summary,
		editing,
		engaged,
		select,
		selected,
		move,
		beginDrag,
		dragTo,
		beginEdit,
		endEdit,
	}), [at, summary, editing, engaged, select, selected, move, beginDrag, dragTo, beginEdit, endEdit]);

	return (
		<LedgerGridContext.Provider value={value}>
			<div className={className}>
				{children}
			</div>
			<LedgerSelectionSummary summary={summary} />
		</LedgerGridContext.Provider>
	);
}
